/**
 * Tanjong Life · "commission a Settlement Assurance Sentinel" — and the honest answer, which is that it is
 * already running.
 *
 * THIS BEAT EXISTS BECAUSE THE ALTERNATIVE WRITES A WRONG RECORD. The surface router reads a
 * making verb plus the word `agent` and hands the sentence to the generic agent builder,
 * which consults nothing scripted, invents a name and persists it into this org. Four of the
 * six autonomous agents here were commissioned before the demo starts, so the phrasing that
 * commissions the other two has to land somewhere truthful rather than somewhere plausible.
 *
 * It creates nothing, and that is deliberate rather than a shortcut. Pointing the commission
 * conversation at a seeded pk would REPLACE the agent row and its whole detail bundle —
 * `demo-born.ts` upserts by pk — and with no boot asset the page would never play its stream
 * again. A fully authored agent would be overwritten to narrate a birth its own v1 release
 * row already carries, with a named proposer and approver on it.
 *
 * So it says so, names what it watches and the one thing it refuses to claim, and opens it.
 */
import type { Scenario } from '../director.js';
import { MAKE_SETTLEMENT_SENTINEL } from './tanjong-make-intents.js';

export const tanjongStandingSettlement: Scenario = {
  id: 'tanjong-standing-settlement',
  orgId: 'tanjong-life',
  /*
   * NO KEYWORDS. `settlement assurance sentinel` as a keyword stole "Build a process for
   * claims payment authorisation with the Settlement Assurance Sentinel driving it" from the
   * build it plainly asks for — these scenarios register before the builds, so a keyword here
   * wins any sentence that merely NAMES the agent. Meaning is the only safe route in.
   */
  keywords: [],
  intents: [MAKE_SETTLEMENT_SENTINEL],
  surface: 'chat',
  steps: [
    {
      do: 'say',
      text: 'The Settlement Assurance Sentinel already exists — it was commissioned by Mireille Anand and approved by Farah Idris, and its v1 has been live over workflow 05 since that release. I am not going to stand a second one up beside it: two agents watching the same queue is the thing this estate is built to prevent, and the second one would have no way of knowing what the first had already opened.',
      sources: [
        'Settlement Assurance Sentinel · v1 live over workflow 05',
        'Proposed by Mireille Anand · approved by Farah Idris',
      ],
      delay: 900,
    },
    {
      do: 'insight',
      title: 'What it already watches',
      body: 'Workflow 05 · claims payment authorisation ends when a person has what they need. This agent watches every settlement released this quarter — the interval from the authorisation event to the release, and the approver pair on each one. It runs 4 probes, 1 held, opens a case at a named person with the evidence attached, and holds all five of its authorities at zero of zero with Halt as the action on breach — it reports, and it decides nothing.',
      sources: ['Process tl-pr14', 'Authorities · 5, every one 0 of 0 · Halt on breach'],
      delay: 1300,
    },
    {
      do: 'say',
      text: 'And the thing worth hearing before you take my word for any of it: Whether the second approver read anything before releasing, at 28 out of 100. Elapsed time is a proxy that fails in both directions, so it reports an interval with both names and both timestamps and never converts it into a percentage. Its overall evaluation is 80.',
      delay: 1300,
    },
    {
      do: 'link',
      label: 'Open Settlement Assurance Sentinel',
      href: '/agents/settlement-assurance-sentinel',
      delay: 600,
    },
  ],
};
